// ─── Le preferenze del socio, scelte sul bot ─────────────────────────────────
//
// 🗣️ 25/09/2026, voci 12 e 13 del bot (mockup approvato): il socio dice **quando
// gioca di solito** (le disponibilità) e **cosa vuole ricevere** (le notifiche).
// *«Se uno è un po' smanettone può andare là dentro e attivarle e disattivarle …
// se no le fa fare al bot, parlandoci.»*
//
// ⭐ **STANNO NELLA SCHEDA DEL SOCIO**, nella colonna `members.preferences` che
// c'era già (jsonb, «dati morbidi su cui nessuno interroga»): la segreteria le
// vede nella scheda, e il bot le rilegge a ogni giro — non ne tiene copia.
// Due chiavi sue, `disponibilita` e `notifiche`: le altre che la colonna porta
// (quelle arrivate col vecchio gestionale) non si toccano.
//
// ⚖️ `null` vuol dire **«non ha ancora scelto»**, ed è diverso da «no»: le
// partite del mattino e i tornei partono solo a chi ha detto sì; i promemoria,
// finché non sceglie, arrivano alle 13.
import { z } from 'zod';
/** Le tre fasce del giorno: mattina fino alle 13, pomeriggio fino alle 18, poi sera. */
export const FASCE_DEL_GIORNO = ['mattina', 'pomeriggio', 'sera'];
const fasce = z.array(z.enum(FASCE_DEL_GIORNO)).max(3);
/** 🙋 Le partite del suo livello che cercano giocatori, ogni mattina alle 8. */
export const SCELTE_PARTITE = ['si', 'no'];
/** 🏆 Tornei ed eventi, alle 8: ogni 15 giorni, ogni 30, o mai. */
export const SCELTE_EVENTI = ['15', '30', 'no'];
/** 💡 I promemoria dell'assistente (amici, test, disponibilità, suggerimenti): alle 8, alle 13, o mai. */
export const SCELTE_PROMEMORIA = ['8', '13', 'no'];
/**
 * 🆕 28/09/2026 (Partite Aperte, mockup approvato): al primo ingresso il bot
 * chiede se il socio vuole **organizzare** partite, **partecipare** o **tutte e
 * due**. La risposta spiega il perché del test del livello con le sue parole.
 */
export const RUOLI_NEL_BOT = ['organizza', 'partecipa', 'entrambi'];
/** Come le preferenze escono sul ponte, verso il bot. */
export const preferenzeDelPonteSchema = z.object({
    availability: z.object({ weekday: fasce, weekend: fasce, updatedAt: z.string().nullable() }).nullable(),
    notifications: z.object({
        matches: z.enum(SCELTE_PARTITE).nullable(),
        events: z.enum(SCELTE_EVENTI).nullable(),
        reminders: z.enum(SCELTE_PROMEMORIA).nullable(),
    }),
    role: z.enum(RUOLI_NEL_BOT).nullable(),
});
/** Quello che il bot scrive: solo le parti che cambiano. */
export const preferenzeDaScrivereSchema = z.object({
    availability: z.object({ weekday: fasce, weekend: fasce }).optional(),
    notifications: z
        .object({
        matches: z.enum(SCELTE_PARTITE),
        events: z.enum(SCELTE_EVENTI),
        reminders: z.enum(SCELTE_PROMEMORIA),
    })
        .partial()
        .optional(),
    role: z.enum(RUOLI_NEL_BOT).optional(),
});
export const esitoPreferenzeSchema = z.object({
    outcome: z.literal('fatto'),
    reason: z.null(),
    preferences: preferenzeDelPonteSchema,
});
// ── Dalla colonna della scheda, e ritorno ────────────────────────────────────
const salvateSchema = z.object({
    disponibilita: z
        .object({
        feriali: fasce.catch([]),
        weekend: fasce.catch([]),
        aggiornateIl: z.string().nullable().catch(null),
    })
        .nullish()
        .catch(null),
    notifiche: z
        .object({
        partite: z.enum(SCELTE_PARTITE).nullish().catch(null),
        eventi: z.enum(SCELTE_EVENTI).nullish().catch(null),
        promemoria: z.enum(SCELTE_PROMEMORIA).nullish().catch(null),
    })
        .nullish()
        .catch(null),
    ruolo: z.enum(RUOLI_NEL_BOT).nullish().catch(null),
});
/**
 * Le preferenze del bot, lette dalla colonna `preferences` della scheda.
 *
 * ⛔ Una colonna scritta a mano, o storta, non fa cadere niente: quello che non
 * si legge vale «non ha ancora scelto».
 */
export function preferenzeDelBot(colonna) {
    const letto = salvateSchema.safeParse(colonna ?? {});
    const s = letto.success ? letto.data : { disponibilita: null, notifiche: null, ruolo: null };
    return {
        availability: s.disponibilita
            ? {
                weekday: s.disponibilita.feriali,
                weekend: s.disponibilita.weekend,
                updatedAt: s.disponibilita.aggiornateIl,
            }
            : null,
        notifications: {
            matches: s.notifiche?.partite ?? null,
            events: s.notifiche?.eventi ?? null,
            reminders: s.notifiche?.promemoria ?? null,
        },
        role: s.ruolo ?? null,
    };
}
/**
 * La colonna dopo una scrittura del bot: le chiavi del bot cambiano, le altre
 * restano come sono.
 */
export function colonnaConLePreferenze(colonna, scritte, adessoIso) {
    const prima = colonna && typeof colonna === 'object' && !Array.isArray(colonna)
        ? colonna
        : {};
    const attuali = preferenzeDelBot(prima);
    const dopo = { ...prima };
    if (scritte.availability) {
        dopo.disponibilita = {
            feriali: ordinate(scritte.availability.weekday),
            weekend: ordinate(scritte.availability.weekend),
            aggiornateIl: adessoIso,
        };
    }
    if (scritte.notifications) {
        const n = { ...attuali.notifications, ...scritte.notifications };
        dopo.notifiche = { partite: n.matches, eventi: n.events, promemoria: n.reminders };
    }
    if (scritte.role)
        dopo.ruolo = scritte.role;
    return dopo;
}
function ordinate(f) {
    return FASCE_DEL_GIORNO.filter((x) => f.includes(x));
}
// ── Come le legge la segreteria ──────────────────────────────────────────────
export function fasceInParole(f) {
    return f.length === 0 ? 'nessuna' : f.join(', ');
}
export const PAROLE_PARTITE = {
    si: 'sì, ogni mattina alle 8',
    no: 'no',
};
export const PAROLE_EVENTI = {
    '15': 'ogni 15 giorni, alle 8',
    '30': 'ogni mese, alle 8',
    no: 'no',
};
export const PAROLE_RUOLO = {
    organizza: 'Organizza',
    partecipa: 'Partecipa',
    entrambi: 'Organizza e partecipa',
};
export const PAROLE_PROMEMORIA = {
    '8': 'sì, alle 8',
    '13': 'sì, alle 13',
    no: 'no',
};
//# sourceMappingURL=preferenze-bot.js.map